import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleDateNightRomantic() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "date-night-romantic-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Bacchus Restaurant & Lounge (Downtown)",
    "section-2": "Ancora Waterfront Dining & Patio (False Creek)",
    "section-3": "The Teahouse in Stanley Park (West End)",
    "section-4": "Seasons in the Park (Queen Elizabeth Park)",
    "section-5": "L’Abattoir (Gastown)",
    "section-6": "Brix & Mortar (Yaletown)",
    "section-7": "Kissa Tanto (Chinatown)",
    "section-8": "Vij’s (South Cambie)",
    "section-9": "Moltaqa Moroccan Restaurant (Yaletown)",
    "section-10": "La Quercia (Kitsilano)",
    "section-11": "The Acorn (Main Street)",
    faq: "FAQ",
    "local-regulations": "Local Regulations",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.destinationvancouver.com/",
      label: "Destination Vancouver",
    },
    {
      url: "https://tourismvancouver.com",
      label: "Tourism Vancouver",
    },
    {
      url: "https://guide.michelin.com/",
      label: "Michelin Guide",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Each location ID corresponds to the section ID so scrolling and highlight can align
  const locations = [
    {
      id: "section-1",
      lat: 49.28237,
      lng: -123.12274,
      popupText: "Bacchus Restaurant & Lounge",
    },
    {
      id: "section-2",
      lat: 49.27384,
      lng: -123.13243,
      popupText: "Ancora Waterfront Dining & Patio (False Creek)",
    },
    {
      id: "section-3",
      lat: 49.30111,
      lng: -123.15889,
      popupText: "The Teahouse in Stanley Park",
    },
    {
      id: "section-4",
      lat: 49.24140,
      lng: -123.11150,
      popupText: "Seasons in the Park (Queen Elizabeth Park)",
    },
    {
      id: "section-5",
      lat: 49.283,
      lng: -123.104,
      popupText: "L’Abattoir (Gastown)",
    },
    {
      id: "section-6",
      lat: 49.27645,
      lng: -123.12167,
      popupText: "Brix & Mortar (Yaletown)",
    },
    {
      id: "section-7",
      lat: 49.28056,
      lng: -123.09806,
      popupText: "Kissa Tanto (Chinatown)",
    },
    {
      id: "section-8",
      lat: 49.2569,
      lng: -123.1155,
      popupText: "Vij’s (South Cambie)",
    },
    {
      id: "section-9",
      lat: 49.27745,
      lng: -123.11990,
      popupText: "Moltaqa Moroccan Restaurant (Yaletown)",
    },
    {
      id: "section-10",
      lat: 49.26833,
      lng: -123.18556,
      popupText: "La Quercia (Kitsilano)",
    },
    {
      id: "section-11",
      lat: 49.2498,
      lng: -123.101,
      popupText: "The Acorn (Main Street)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Date Night in Vancouver: 5+ Romantic Restaurants</title>
        <meta
          name="description"
          content="Explore Vancouver's most romantic date-night restaurants, offering waterfront dining, cozy candlelit bistros, and vegetarian-friendly spots."
        />
        <meta name="keywords" content="restaurants, romantic, date night, Vancouver, dining" />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/date-night-romantic-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Date Night in Vancouver: 5+ Romantic Restaurants"
        />
        <meta
          property="og:description"
          content="Explore Vancouver's most romantic date-night restaurants, from waterfront patios to cozy candlelit lounges."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/date-night-romantic-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/date-night-romantic-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Date Night in Vancouver: 5+ Romantic Restaurants"
        />
        <meta
          name="twitter:description"
          content="Find the perfect spot for your next date night in Vancouver with these curated romantic restaurants."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/date-night-romantic-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="A romantic table setting overlooking Vancouver's skyline"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver boasts a vibrant culinary scene that caters to every
              taste—from upscale waterfront seafood to cozy candlelit bistros.
              Whether you’re celebrating an anniversary or looking for a
              delightful evening out, the city offers plenty of romantic
              restaurants. Below is a curated selection featuring different
              cuisines, ambiances, and budgets to help you plan the perfect date
              night in Vancouver.
            </p>

            {/* SECTION: Bacchus Restaurant & Lounge */}
            <h2 id="section-1">Bacchus Restaurant & Lounge (Downtown)</h2>
            <p
              ref={(el) => setSpotRef("section-1", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Bacchus Restaurant & Lounge</strong>{" "}
              <Marker
                coordY={49.28237}
                coordX={-123.12274}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;is nestled in the Wedgewood Hotel, offering an old-world
              charm with live piano music and luxurious décor. The
              French-inspired menu focuses on locally sourced ingredients,
              paired with an extensive wine list. Perfect for a special
              celebration or an upscale night out. Dress code is typically
              smart-casual to formal, and reservations are recommended.
            </p>

            {/* SECTION: Ancora Waterfront Dining & Patio */}
            <h2 id="section-2">Ancora Waterfront Dining & Patio (False Creek)</h2>
            <p
              ref={(el) => setSpotRef("section-2", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Ancora Waterfront Dining &amp; Patio</strong>{" "}
              <Marker
                coordY={49.27384}
                coordX={-123.13243}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;showcases Peruvian-Japanese fusion cuisine with a West
              Coast twist. Situated along False Creek, the restaurant boasts a
              stunning view of the water and city skyline. With sustainable
              seafood, an elegant raw bar, and a dreamy patio, Ancora is ideal
              for romantic waterfront dinners—especially during sunset.
            </p>

            {/* SECTION: The Teahouse in Stanley Park */}
            <h2 id="section-3">The Teahouse in Stanley Park (West End)</h2>
            <p
              ref={(el) => setSpotRef("section-3", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>The Teahouse</strong>{" "}
              <Marker
                coordY={49.30111}
                coordX={-123.15889}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;at Ferguson Point offers an ocean backdrop inside the iconic
              Stanley Park. Serving classic West Coast dishes, it’s known for
              scenic sunsets over English Bay. The warm interior and garden
              patio make for an inviting atmosphere, perfect for a laid-back yet
              romantic evening.
            </p>

            {/* SECTION: Seasons in the Park */}
            <h2 id="section-4">Seasons in the Park (Queen Elizabeth Park)</h2>
            <p
              ref={(el) => setSpotRef("section-4", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Seasons in the Park</strong>{" "}
              <Marker
                coordY={49.24140}
                coordX={-123.11150}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;sits atop Queen Elizabeth Park, offering panoramic views of
              the city skyline and North Shore mountains. The restaurant serves
              West Coast fare—steaks, seafood, and local wines. Its hilltop
              vantage point and floor-to-ceiling windows create a spectacular
              dining ambiance, especially for special occasions.
            </p>

            {/* SECTION: L’Abattoir */}
            <h2 id="section-5">L’Abattoir (Gastown)</h2>
            <p
              ref={(el) => setSpotRef("section-5", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>L’Abattoir</strong>{" "}
              <Marker
                coordY={49.283}
                coordX={-123.104}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;is housed in a 19th-century building in historic Gastown. It
              blends French cooking techniques with West Coast flavors in a
              chic, industrial setting. The mezzanine and atrium seating offer
              intimate corners for a date night. Reservations are recommended,
              and cocktail lovers will appreciate the creative drink menu.
            </p>

            {/* SECTION: Brix & Mortar */}
            <h2 id="section-6">Brix & Mortar (Yaletown)</h2>
            <p
              ref={(el) => setSpotRef("section-6", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Brix &amp; Mortar</strong>{" "}
              <Marker
                coordY={49.27645}
                coordX={-123.12167}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;occupies a 1912 heritage building in Yaletown, featuring a
              glass-covered courtyard lit by string lights and chandeliers.
              Serving modern Canadian cuisine, this restaurant’s romantic
              setting is perfect for those seeking a blend of heritage charm and
              contemporary flair.
            </p>

            {/* SECTION: Kissa Tanto */}
            <h2 id="section-7">Kissa Tanto (Chinatown)</h2>
            <p
              ref={(el) => setSpotRef("section-7", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Kissa Tanto</strong>{" "}
              <Marker
                coordY={49.28056}
                coordX={-123.09806}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;is a hip restaurant blending Japanese and Italian cuisines,
              tucked away in a second-floor space in Chinatown. The retro,
              sultry jazz club ambiance sets the stage for a unique culinary
              adventure—think miso-infused pasta, inventive cocktails, and a
              cozy, dimly lit interior. Reservation is crucial due to high
              demand.
            </p>

            {/* SECTION: Vij’s */}
            <h2 id="section-8">Vij’s (South Cambie)</h2>
            <p
              ref={(el) => setSpotRef("section-8", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Vij’s</strong>{" "}
              <Marker
                coordY={49.2569}
                coordX={-123.1155}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;has revolutionized upscale Indian cuisine in Vancouver. The
              lively yet intimate atmosphere, creative curries, and fantastic
              service make it a great pick for food-lovers on date night. They
              now accept reservations, but walk-ins can still expect a warm
              welcome and possibly a short wait with complimentary chai.
            </p>

            {/* SECTION: Moltaqa Moroccan Restaurant */}
            <h2 id="section-9">Moltaqa Moroccan Restaurant (Yaletown)</h2>
            <p
              ref={(el) => setSpotRef("section-9", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Moltaqa</strong>{" "}
              <Marker
                coordY={49.27745}
                coordX={-123.1199}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;brings the flavors and ambiance of Morocco to Vancouver with
              vibrant lanterns, traditional tagines, and cozy nooks. An
              affordable yet exotic option for couples who want to explore new
              tastes in an intimate setting.
            </p>

            {/* SECTION: La Quercia */}
            <h2 id="section-10">La Quercia (Kitsilano)</h2>
            <p
              ref={(el) => setSpotRef("section-10", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>La Quercia</strong>{" "}
              <Marker
                coordY={49.26833}
                coordX={-123.18556}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;is a small, intimate Italian restaurant serving
              seasonally-driven cuisine. Featuring handmade pastas and a
              family-style tasting menu, it offers a cozy trattoria experience
              in Kitsilano. Advanced booking is recommended due to limited
              seating.
            </p>

            {/* SECTION: The Acorn */}
            <h2 id="section-11">The Acorn (Main Street)</h2>
            <p
              ref={(el) => setSpotRef("section-11", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>The Acorn</strong>{" "}
              <Marker
                coordY={49.2498}
                coordX={-123.101}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              &nbsp;is a vegetarian restaurant revered for its innovative,
              plant-based dishes that can intrigue even devoted carnivores.
              Located on Main Street, it provides an upscale yet laid-back vibe
              with farm-to-table produce. Perfect for couples embracing a
              vegetarian or vegan lifestyle—or anyone looking for a creative,
              healthier twist on date night cuisine.
            </p>

            {/* FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Which romantic restaurant has the best view in Vancouver?</h3>
              <p>
                Many places offer stunning views. <strong>Seasons in the Park</strong>
                in Queen Elizabeth Park provides panoramic city vistas, while
                <strong>Ancora</strong> offers a waterfront view of False Creek. 
                Each location is unique; choose based on whether you prefer a
                skyline or water backdrop.
              </p>

              <h3>2. Do I need reservations?</h3>
              <p>
                Yes. Most of these romantic spots are popular and book up quickly,
                especially on weekends. Check the restaurant's website for
                availability or call in advance. Some allow walk-ins but wait
                times can be significant during peak hours.
              </p>

              <h3>3. Which options are budget-friendly?</h3>
              <p>
                <strong>Moltaqa Moroccan Restaurant</strong> in Yaletown offers
                an affordable yet exotic dining experience. Also, heading to
                these restaurants for lunch or happy hour (if available) can be
                more budget-friendly than a full-course dinner. 
              </p>
            </div>

            {/* Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              Most Vancouver restaurants follow strict food safety standards
              under Vancouver Coastal Health. Alcohol service typically ends by
              midnight. Some patios may have seasonal hours or noise
              restrictions. Always check current guidelines, especially if
              COVID-19 or other public health measures are in effect. 
            </p>

            {/* Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Whether it’s a lavish anniversary celebration or a cozy night out,
              Vancouver offers a diverse range of romantic date-night
              experiences. From waterfront fusion cuisine to candlelit
              vegetarian gems, these restaurants are sure to leave a lasting
              impression. Make your reservation, dress up (or down, Vancouver
              style), and enjoy an unforgettable evening in one of the world’s
              most scenic cities. 
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}